import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Typography, Tooltip, alpha } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { DataContext } from "./index";
import { ASSETICONBASEURL } from "../../CoreDataService";
import axios from "axios";

// Static curated dictionary for instant loading of top assets
const CURATED_COIN_INFO = {
  BTC: {
    name: "Bitcoin",
    symbol: "BTC",
    rank: 1,
    marketDominance: 59.8,
    circulatingSupply: 19800000,
    maxSupply: 21000000,
    totalSupply: 19800000,
    platformConcentration: 0.63,
    website: "https://bitcoin.org",
    whitepaper: "https://bitcoin.org/bitcoin.pdf",
    explorer: "https://blockchain.info",
    research: "https://academy.binance.com/en/articles/what-is-bitcoin",
    description:
      "Bitcoin is a decentralized cryptocurrency originally described in a 2008 whitepaper by a person, or group of people, using the alias Satoshi Nakamoto. It was launched soon after, in January 2009. Bitcoin is a peer-to-peer online currency, meaning that all transactions happen directly between equal, independent network participants, without the need for any intermediary to permit or facilitate them.",
  },
  ETH: {
    name: "Ethereum",
    symbol: "ETH",
    rank: 2,
    marketDominance: 14.2,
    circulatingSupply: 120200000,
    maxSupply: null,
    totalSupply: 120200000,
    platformConcentration: 0.58,
    website: "https://ethereum.org",
    whitepaper: "https://ethereum.org/en/whitepaper/",
    explorer: "https://etherscan.io",
    research: "https://academy.binance.com/en/articles/what-is-ethereum",
    description:
      "Ethereum is a decentralized, open-source blockchain with smart contract functionality. Ether is the native cryptocurrency of the platform. Among cryptocurrencies, Ether is second only to Bitcoin in market capitalization. Ethereum was conceived in 2013 by programmer Vitalik Buterin and enables decentralized applications (dApps) and decentralized finance (DeFi).",
  },
  SOL: {
    name: "Solana",
    symbol: "SOL",
    rank: 5,
    marketDominance: 3.9,
    circulatingSupply: 471000000,
    maxSupply: null,
    totalSupply: 588000000,
    platformConcentration: 0.52,
    website: "https://solana.com",
    whitepaper: "https://solana.com/solana-whitepaper.pdf",
    explorer: "https://solscan.io",
    research: "https://academy.binance.com/en/articles/what-is-solana-sol",
    description:
      "Solana is a highly functional open source project that banks on blockchain technology’s permissionless nature to provide decentralized finance (DeFi) solutions. Solana utilizes an innovative hybrid consensus model combining a unique proof-of-history (PoH) algorithm with the lightning-fast synchronization engine of proof-of-stake (PoS).",
  },
  BNB: {
    name: "BNB",
    symbol: "BNB",
    rank: 4,
    marketDominance: 3.5,
    circulatingSupply: 144000000,
    maxSupply: 200000000,
    totalSupply: 144000000,
    platformConcentration: 0.61,
    website: "https://binance.com",
    whitepaper: "https://www.binance.com/en/support/faq",
    explorer: "https://bscscan.com",
    research: "https://academy.binance.com/en/articles/what-is-bnb",
    description:
      "BNB is the native cryptocurrency of the BNB Chain ecosystem, one of the world's most popular blockchains for smart contracts, Web3 gaming, and DeFi applications. It powers the Binance decentralized exchange and offers fee discounts for platform trades.",
  },
  XRP: {
    name: "XRP",
    symbol: "XRP",
    rank: 6,
    marketDominance: 2.1,
    circulatingSupply: 56900000000,
    maxSupply: 100000000000,
    totalSupply: 99987000000,
    platformConcentration: 0.49,
    website: "https://ripple.com/xrp",
    whitepaper: "https://ripple.com/files/ripple_consensus_whitepaper.pdf",
    explorer: "https://xrpscan.com",
    research: "https://academy.binance.com/en/articles/what-is-ripple-and-xrp",
    description:
      "XRP is a digital asset built for payments. It is the native cryptocurrency on the XRP Ledger—an open-source, permissionless and decentralized blockchain technology that can settle transactions in 3-5 seconds with near-zero network fees.",
  },
  DOGE: {
    name: "Dogecoin",
    symbol: "DOGE",
    rank: 8,
    marketDominance: 1.8,
    circulatingSupply: 146800000000,
    maxSupply: null,
    totalSupply: 146800000000,
    platformConcentration: 0.44,
    website: "https://dogecoin.com",
    whitepaper: "https://dogecoin.com",
    explorer: "https://dogechain.info",
    research: "https://academy.binance.com/en/articles/what-is-dogecoin",
    description:
      "Dogecoin is based on the popular 'doge' Internet meme and features a Shiba Inu on its logo. The open-source digital currency was created by Billy Markus from Portland, Oregon and Jackson Palmer from Sydney, Australia, and was forked from Litecoin in December 2013.",
  },
  ADA: {
    name: "Cardano",
    symbol: "ADA",
    rank: 9,
    marketDominance: 1.4,
    circulatingSupply: 35770000000,
    maxSupply: 45000000000,
    totalSupply: 35770000000,
    platformConcentration: 0.41,
    website: "https://cardano.org",
    whitepaper: "https://cardano.org/research",
    explorer: "https://cardanoscan.io",
    research: "https://academy.binance.com/en/articles/what-is-cardano-ada",
    description:
      "Cardano is a proof-of-stake blockchain platform that says its goal is to allow changemakers, innovators and visionaries to bring about positive global change through peer-reviewed academic research.",
  },
  AVAX: {
    name: "Avalanche",
    symbol: "AVAX",
    rank: 12,
    marketDominance: 1.1,
    circulatingSupply: 406000000,
    maxSupply: 720000000,
    totalSupply: 445000000,
    platformConcentration: 0.38,
    website: "https://avax.network",
    whitepaper: "https://www.avalabs.org/whitepapers",
    explorer: "https://snowtrace.io",
    research: "https://academy.binance.com/en/articles/what-is-avalanche-avax",
    description:
      "Avalanche is a layer one blockchain that functions as a platform for decentralized applications and custom blockchain networks. It is one of Ethereum’s rivals, aiming to unseat Ethereum as the most popular blockchain for smart contracts.",
  },
  LINK: {
    name: "Chainlink",
    symbol: "LINK",
    rank: 15,
    marketDominance: 0.8,
    circulatingSupply: 608000000,
    maxSupply: 1000000000,
    totalSupply: 1000000000,
    platformConcentration: 0.35,
    website: "https://chain.link",
    whitepaper: "https://chain.link/whitepaper",
    explorer: "https://etherscan.io/token/0x514910771af9ca656af840dff83e8264ecf986ca",
    research: "https://academy.binance.com/en/articles/what-is-chainlink-link",
    description:
      "Chainlink is a blockchain abstraction layer that enables universally connected smart contracts. Through a decentralized oracle network, Chainlink allows blockchains to securely interact with external data feeds, events, and payment methods.",
  },
  USDT: {
    name: "Tether",
    symbol: "USDT",
    rank: 3,
    marketDominance: 5.1,
    circulatingSupply: 120000000000,
    maxSupply: null,
    totalSupply: 120000000000,
    platformConcentration: 0.72,
    website: "https://tether.to",
    whitepaper: "https://tether.to/en/whitepaper",
    explorer: "https://etherscan.io/token/0xdac17f958d2ee523a2206206994597c13d831ec7",
    research: "https://academy.binance.com/en/articles/what-is-tether-usdt",
    description:
      "Tether (USDT) is a stablecoin pegged to the US Dollar. It is designed to maintain a 1:1 ratio with the USD and is widely used across crypto exchanges as a base liquidity currency.",
  },
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return "--";
  if (num >= 1e12) return `$${(num / 1e12).toFixed(decimals)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(decimals)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(decimals)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(decimals)}K`;
  return `$${Number(num).toLocaleString("en-US", { maximumFractionDigits: decimals })}`;
};

const formatSupply = (num, symbol = "") => {
  if (num === null || num === undefined || isNaN(num)) return "--";
  return `${Number(num).toLocaleString("en-US", { maximumFractionDigits: 0 })} ${symbol}`.trim();
};

const SpotCoinInfo = () => {
  const { currencycode = "BTC", basecurrency = "USDT", ltp, chartData } = useContext(DataContext) || {};
  const { btncolor } = useSelector((state) => state);

  const [activeSubTab, setActiveSubTab] = useState("Info"); // "Info" | "Trading Parameters"
  const [isExpandedIntro, setIsExpandedIntro] = useState(false);
  const [liveData, setLiveData] = useState(null);

  const coinCode = (currencycode || "BTC").toUpperCase();

  // Load curated baseline info
  const baseline = useMemo(() => {
    if (CURATED_COIN_INFO[coinCode]) {
      return CURATED_COIN_INFO[coinCode];
    }
    // Dynamic fallback for custom/new tokens
    return {
      name: coinCode,
      symbol: coinCode,
      rank: "--",
      marketDominance: "--",
      circulatingSupply: null,
      maxSupply: null,
      totalSupply: null,
      platformConcentration: 0.5,
      website: `https://${coinCode.toLowerCase()}.org`,
      whitepaper: `https://${coinCode.toLowerCase()}.org/whitepaper`,
      explorer: `https://etherscan.io/search?q=${coinCode}`,
      research: null,
      description: `${coinCode} is a digital asset listed for spot trading on this exchange. Track its live price, order book depth, and liquidity on the spot dashboard.`,
    };
  }, [coinCode]);

  // Attempt to fetch fresh data from public CoinGecko API in client browser (if available)
  useEffect(() => {
    let isMounted = true;
    const fetchCoinGeckoData = async () => {
      try {
        // Map common codes to CoinGecko IDs
        const idMap = {
          BTC: "bitcoin",
          ETH: "ethereum",
          SOL: "solana",
          BNB: "binancecoin",
          XRP: "ripple",
          DOGE: "dogecoin",
          ADA: "cardano",
          AVAX: "avalanche-2",
          LINK: "chainlink",
          TRX: "tron",
          DOT: "polkadot",
          MATIC: "matic-network",
          USDT: "tether",
          LTC: "litecoin",
          SHIB: "shiba-inu",
          PEPE: "pepe",
          NEAR: "near",
        };

        const targetId = idMap[coinCode] || coinCode.toLowerCase();
        const res = await axios.get(
          `https://api.coingecko.com/api/v3/coins/${targetId}?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false`,
          { timeout: 4000 }
        );

        if (isMounted && res.data) {
          const d = res.data;
          setLiveData({
            name: d.name || baseline.name,
            symbol: (d.symbol || coinCode).toUpperCase(),
            rank: d.market_cap_rank || baseline.rank,
            image: d.image?.large || d.image?.small || null,
            marketCap: d.market_data?.market_cap?.usd,
            fdv: d.market_data?.fully_diluted_valuation?.usd,
            volume24h: d.market_data?.total_volume?.usd,
            circulatingSupply: d.market_data?.circulating_supply,
            totalSupply: d.market_data?.total_supply,
            maxSupply: d.market_data?.max_supply,
            website: d.links?.homepage?.[0] || baseline.website,
            whitepaper: d.links?.whitepaper || baseline.whitepaper,
            explorer: d.links?.blockchain_site?.[0] || baseline.explorer,
            description: d.description?.en
              ? d.description.en.replace(/<[^>]*>?/gm, "").slice(0, 700) + "..."
              : baseline.description,
          });
        }
      } catch (err) {
        // Quiet fallback to curated/calculated data
      }
    };

    fetchCoinGeckoData();
    return () => {
      isMounted = false;
    };
  }, [coinCode, baseline]);

  // Derived metrics combining live API / curated data with Paybito real-time LTP
  const effectivePrice = parseFloat(ltp || chartData?.ltp) || 0;
  const rank = liveData?.rank || baseline.rank;
  const name = liveData?.name || baseline.name;
  const circSupply = liveData?.circulatingSupply || baseline.circulatingSupply;
  const maxSupply = liveData?.maxSupply || baseline.maxSupply;
  const totalSupply = liveData?.totalSupply || baseline.totalSupply;

  // Compute market cap from price * supply if not provided by API
  const marketCap =
    liveData?.marketCap ||
    (circSupply && effectivePrice > 0 ? circSupply * effectivePrice : null);

  const fdv =
    liveData?.fdv ||
    (maxSupply && effectivePrice > 0
      ? maxSupply * effectivePrice
      : totalSupply && effectivePrice > 0
      ? totalSupply * effectivePrice
      : marketCap);

  const volume24h =
    liveData?.volume24h ||
    (chartData?.volume && effectivePrice > 0
      ? parseFloat(chartData.volume) * effectivePrice
      : null);

  const volToMcap =
    volume24h && marketCap && marketCap > 0
      ? ((volume24h / marketCap) * 100).toFixed(2) + "%"
      : "--";

  const marketDominance = baseline.marketDominance
    ? `${baseline.marketDominance}%`
    : "--";

  const websiteUrl = liveData?.website || baseline.website;
  const whitepaperUrl = liveData?.whitepaper || baseline.whitepaper;
  const explorerUrl = liveData?.explorer || baseline.explorer;
  const descriptionText = liveData?.description || baseline.description;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: "var(--body-color, #181a20)",
        color: "var(--text-color, #eaecef)",
        p: { xs: 2, md: 3 },
        boxSizing: "border-box",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(125,125,125,0.25)",
          borderRadius: "4px",
        },
      }}
    >
      {/* Top Sub-tabs (Info | Trading Parameters) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(125,125,125,0.15)",
          pb: 1.5,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box
            onClick={() => setActiveSubTab("Info")}
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              cursor: "pointer",
              color:
                activeSubTab === "Info"
                  ? btncolor || "#f0b90b"
                  : "var(--text-color-less, #848e9c)",
              borderBottom: activeSubTab === "Info" ? `2px solid ${btncolor || "#f0b90b"}` : "none",
              pb: "6px",
              transition: "all 0.15s ease",
            }}
          >
            Info
          </Box>
          <Box
            onClick={() => setActiveSubTab("Trading Parameters")}
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              color:
                activeSubTab === "Trading Parameters"
                  ? btncolor || "#f0b90b"
                  : "var(--text-color-less, #848e9c)",
              borderBottom:
                activeSubTab === "Trading Parameters"
                  ? `2px solid ${btncolor || "#f0b90b"}`
                  : "none",
              pb: "6px",
              transition: "all 0.15s ease",
            }}
          >
            Trading Parameters
          </Box>
        </Box>
      </Box>

      {/* Coin Title Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            backgroundColor: alpha(btncolor || "#f0b90b", 0.12),
            border: `1.5px solid ${alpha(btncolor || "#f0b90b", 0.3)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
            p: "3px",
            boxSizing: "border-box",
          }}
        >
          <img
            src={`${ASSETICONBASEURL}${coinCode}.png`}
            alt={coinCode}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: "50%",
            }}
            onError={(e) => {
              if (liveData?.image && e.target.src !== liveData.image) {
                e.target.src = liveData.image;
              } else {
                e.target.style.display = "none";
                if (e.target.nextElementSibling) {
                  e.target.nextElementSibling.style.display = "flex";
                }
              }
            }}
          />
          <Box
            component="span"
            sx={{
              display: "none",
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "13px",
              color: btncolor || "#f0b90b",
            }}
          >
            {coinCode.slice(0, 3)}
          </Box>
        </Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "var(--text-color, #fff)" }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "var(--text-color-less, #848e9c)" }}>
              {coinCode}
            </Typography>
            {rank !== "--" && (
              <Box
                sx={{
                  px: 1,
                  py: "2px",
                  borderRadius: "4px",
                  fontSize: "11px",
                  fontWeight: 700,
                  backgroundColor: "rgba(125,125,125,0.18)",
                  color: "var(--text-color, #eaecef)",
                }}
              >
                No. {rank}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content Layout */}
      {activeSubTab === "Info" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
            gap: 4,
          }}
        >
          {/* Left Column: Key Metrics Table */}
          <Box>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--text-color-less, #848e9c)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 1.5,
              }}
            >
              Key Metrics
            </Typography>

            <Box
              sx={{
                backgroundColor: "rgba(125,125,125,0.05)",
                borderRadius: "8px",
                p: "6px 14px",
                border: "1px solid rgba(125,125,125,0.12)",
              }}
            >
              <MetricRow label="Rank" value={rank !== "--" ? `No. ${rank}` : "--"} />
              <MetricRow
                label="Market Capitalization"
                value={marketCap ? formatNumber(marketCap) : "--"}
                tooltip="Total value of all circulating coins (Price x Circulating Supply)"
              />
              <MetricRow
                label="Fully Diluted Market Cap"
                value={fdv ? formatNumber(fdv) : "--"}
                tooltip="Estimated market cap if max supply were in circulation (Price x Max Supply)"
              />
              <MetricRow
                label="Market Dominance"
                value={marketDominance}
                tooltip="Proportion of the entire crypto market value held by this asset"
              />
              <MetricRow
                label="24h Volume"
                value={volume24h ? formatNumber(volume24h) : "--"}
                tooltip="Total trading volume across all markets in the last 24 hours"
              />
              <MetricRow
                label="Volume / Market Cap"
                value={volToMcap}
                tooltip="Ratio of 24h volume to total market cap (indicator of liquidity)"
              />
              <MetricRow
                label="Circulating Supply"
                value={formatSupply(circSupply, coinCode)}
                tooltip="Number of coins currently circulating in public hands"
              />
              <MetricRow
                label="Maximum Supply"
                value={maxSupply ? formatSupply(maxSupply, coinCode) : "Unlimited"}
                tooltip="Theoretical maximum number of coins that will ever exist"
              />
              <MetricRow
                label="Total Supply"
                value={totalSupply ? formatSupply(totalSupply, coinCode) : "--"}
                tooltip="Total amount of coins created minus any burned coins"
              />
              <MetricRow
                label="Platform Concentration"
                value={baseline.platformConcentration || "0.60"}
                tooltip="Estimated liquidity depth and distribution across major order books"
                isLast
              />
            </Box>
          </Box>

          {/* Right Column: Links, Resources & Description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Links and Resources */}
            <Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "var(--text-color-less, #848e9c)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 1.5,
                }}
              >
                Links & Resources
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                <LinkRow label="Website">
                  {websiteUrl && (
                    <ResourcePill href={websiteUrl} label="Official Website" icon={<LanguageIcon sx={{ fontSize: 13 }} />} />
                  )}
                  {whitepaperUrl && (
                    <ResourcePill
                      href={whitepaperUrl}
                      label="Whitepaper"
                      icon={<DescriptionOutlinedIcon sx={{ fontSize: 13 }} />}
                    />
                  )}
                </LinkRow>

                <LinkRow label="Block Explorer">
                  {explorerUrl && (
                    <ResourcePill href={explorerUrl} label="Explorer" icon={<SearchIcon sx={{ fontSize: 13 }} />} />
                  )}
                </LinkRow>

                {baseline.research && (
                  <LinkRow label="Research">
                    <ResourcePill href={baseline.research} label="Project Report" icon={<OpenInNewIcon sx={{ fontSize: 13 }} />} />
                  </LinkRow>
                )}
              </Box>
            </Box>

            {/* Disclaimer card */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: "6px",
                backgroundColor: "rgba(125,125,125,0.06)",
                border: "1px solid rgba(125,125,125,0.1)",
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "var(--text-color-less, #848e9c)", lineHeight: 1.5 }}>
                * Underlying data is sourced and aggregated from CoinGecko, CoinMarketCap, and exchange public metrics.
                This information is presented on an &apos;as is&apos; basis for informational reference only.
              </Typography>
            </Box>

            {/* Project Intro / Description */}
            <Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "var(--text-color-less, #848e9c)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 1,
                }}
              >
                Intro
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  lineHeight: 1.6,
                  color: "var(--text-color, #eaecef)",
                  opacity: 0.9,
                }}
              >
                {isExpandedIntro || descriptionText.length < 280
                  ? descriptionText
                  : `${descriptionText.slice(0, 280)}... `}
                {descriptionText.length >= 280 && (
                  <Box
                    component="span"
                    onClick={() => setIsExpandedIntro(!isExpandedIntro)}
                    sx={{
                      color: btncolor || "#f0b90b",
                      cursor: "pointer",
                      fontWeight: 600,
                      ml: 0.5,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {isExpandedIntro ? "Show Less" : "Learn More"}
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Trading Parameters Tab */
        <Box sx={{ maxWidth: 700 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-color-less, #848e9c)", mb: 2 }}>
            Spot Market Trading Limits for {coinCode}/{basecurrency}
          </Typography>
          <Box
            sx={{
              backgroundColor: "rgba(125,125,125,0.05)",
              borderRadius: "8px",
              p: "8px 16px",
              border: "1px solid rgba(125,125,125,0.12)",
            }}
          >
            <MetricRow label="Base Asset" value={coinCode} />
            <MetricRow label="Quote Asset" value={basecurrency} />
            <MetricRow label="Price Precision" value={`${localStorage.getItem("priceprc") || 6} Decimals`} />
            <MetricRow label="Amount Precision" value={`${localStorage.getItem("amountprc") || 6} Decimals`} />
            <MetricRow label="Min Order Amount" value={`0.0001 ${coinCode}`} />
            <MetricRow label="Max Market Order" value={`1,000 ${coinCode}`} />
            <MetricRow label="Supported Order Types" value="Market, Limit, Stop-Limit, IOC, FOK, OCO" isLast />
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Helpers & Micro-components ──────────────────────────────────────────────────
const MetricRow = ({ label, value, tooltip, isLast = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.1,
      borderBottom: isLast ? "none" : "1px solid rgba(125,125,125,0.09)",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
      <Typography sx={{ fontSize: "13px", color: "var(--text-color-less, #848e9c)" }}>{label}</Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13, color: "var(--text-color-less, #848e9c)", opacity: 0.7 }} />
        </Tooltip>
      )}
    </Box>
    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-color, #fff)" }}>
      {value}
    </Typography>
  </Box>
);

const LinkRow = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
    <Typography sx={{ width: 110, fontSize: "13px", color: "var(--text-color-less, #848e9c)" }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>{children}</Box>
  </Box>
);

const ResourcePill = ({ href, label, icon }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      px: "10px",
      py: "4px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: 500,
      backgroundColor: "rgba(125,125,125,0.12)",
      color: "var(--text-color, #eaecef)",
      textDecoration: "none",
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: "rgba(125,125,125,0.22)",
        color: "#fff",
      },
    }}
  >
    {icon}
    {label}
  </Box>
);

export default SpotCoinInfo;
